/**
 * NodeSwitch — označení aktuálního nodu.
 * Klik na jméno: publikované veřejné UI, přepne celou stránku.
 * Na remote/spoke: Home na centrální veřejné UI (šedé, když URL není).
 *
 * MCP-only přepínání (call-target) je v kódu, ale nepoužívá se —
 * shazovalo levé menu / session.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cable, Check, ChevronDown, FlaskConical, Home, Server } from 'lucide-react';
import { mcpCall } from '../lib/mcp';
import { uiScope } from '../lib/ui-scope';
import { useMeshPeers } from '../widgets/NodePicker';
import {
  browserOnInternalNetwork,
  buildUiSwitchNodes,
  homeUiHref,
  nodeDisplayLabel,
  resolveHomeUi,
  type SwitchNode,
} from '../widgets/node-targets';

const ENV_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  production: { bg: 'bg-green-500/15 border-green-500/30', text: 'text-green-400', label: 'PROD' },
  staging: {
    bg: 'bg-yellow-500/15 border-yellow-500/30',
    text: 'text-yellow-400',
    label: 'STAGING',
  },
  dev: { bg: 'bg-blue-500/15 border-blue-500/30', text: 'text-blue-400', label: 'DEV' },
};

function useServerInfo() {
  return useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      mcpCall<{
        env: string;
        label: string | null;
        hostname: string;
        version?: string;
        spoke_hub?: string | null;
        home_ui?: string | null;
        home_ui_published?: boolean;
      }>(
        'acm-self.ui_context',
        {},
      ),
    staleTime: 60_000,
  });
}

function EnvBadge({ env, label }: { env?: string | null; label?: string | null }) {
  if (!env) return null;
  const style = ENV_STYLES[env] ?? ENV_STYLES.dev;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold tracking-wider ${style.bg} ${style.text}`}
    >
      {env === 'dev' && <FlaskConical size={8} />}
      {label ?? style.label}
    </span>
  );
}

export function NodeSwitch({ variant = 'default' }: { variant?: 'default' | 'phone' }) {
  const { t } = useTranslation();
  const phone = variant === 'phone';
  const scope = uiScope();
  const { data: info } = useServerInfo();
  const { data: peers } = useMeshPeers({ includeLocal: true });

  const list = useMemo(() => (Array.isArray(peers) ? peers : []), [peers]);

  const uiNodes = useMemo(() => {
    const here = window.location.origin.replace(/\/$/, '') + '/';
    return buildUiSwitchNodes(list, {
      hereHost: window.location.hostname,
      hereUrl: here,
      scope,
      // Interní fallback (https://<host>:9080) jen když je prohlížeč sám
      // uvnitř sítě — zvenku by privátní IP vedla do prázdna.
      allowInternal: browserOnInternalNetwork(window.location),
      hereLabel: info?.label ?? info?.hostname ?? null,
    });
  }, [list, scope, info?.label, info?.hostname]);

  const hostPeer = list.find((p) => p.is_local);
  const current = uiNodes.find((n) => n.current) ?? uiNodes[0];
  const displayLabel = current?.label
    ?? (hostPeer ? nodeDisplayLabel(hostPeer) : (info?.hostname ?? '…'));
  const displayEnv = current?.env ?? hostPeer?.env ?? info?.env ?? null;

  const isSpoke = !!info?.spoke_hub;
  const canUi = uiNodes.length > 1;
  const rememberedHome = (() => {
    try {
      return sessionStorage.getItem('acm_home_ui');
    } catch {
      return null;
    }
  })();
  const home = resolveHomeUi({
    spokeHub: info?.spoke_hub ?? rememberedHome ?? 'hub',
    peers: list,
    scope,
    explicitUrl: info?.home_ui ?? rememberedHome,
    explicitPublished: !!(info?.home_ui_published || rememberedHome),
    allowInternal: browserOnInternalNetwork(window.location),
  });
  const homeHref = homeUiHref(home);
  const showHome = isSpoke || !canUi;

  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; openUp: boolean } | null>(
    null,
  );

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(phone ? 280 : 260, Math.max(r.width, phone ? 180 : 200));
    const spaceBelow = window.innerHeight - r.bottom - 6;
    const openUp = spaceBelow < 160;
    let left = r.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - 8 - width;
    if (left < 8) left = 8;
    setPos({
      top: openUp ? r.top - 6 : r.bottom + 6,
      left,
      width,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const goPublic = async (url: string) => {
    try {
      const hop = await mcpCall<{ ok?: boolean; ticket?: string }>(
        'identity.session_hop_create',
        {},
        { silent: true },
      );
      if (hop?.ok && hop.ticket) {
        const dest = new URL('/auth/session-hop', url);
        dest.searchParams.set('ticket', hop.ticket);
        dest.searchParams.set('from', window.location.origin);
        dest.searchParams.set('return', url);
        window.location.assign(dest.toString());
        return;
      }
    } catch { /* cílový nod si vyžádá login sám */ }
    window.location.assign(url);
  };

  const goUi = (node: SwitchNode) => {
    if (!node.url || node.current) {
      setOpen(false);
      return;
    }
    void goPublic(node.url);
  };

  const menu = open && pos && canUi && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        role='listbox'
        aria-label={t('layout.nodeSwitchAria', 'Přepnout nod')}
        className='fixed z-[200] rounded-xl border border-gray-800/90 bg-gray-950/98 p-1 shadow-2xl backdrop-blur-md'
        style={{
          top: pos.openUp ? undefined : pos.top,
          bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
          left: pos.left,
          width: pos.width,
        }}
      >
        {uiNodes.map((n) => (
          <button
            key={n.key}
            type='button'
            role='option'
            aria-selected={n.current}
            onClick={() => goUi(n)}
            className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
              n.current ? 'bg-gray-800/50' : 'hover:bg-gray-800/40'
            }`}
          >
            <span className='min-w-0 flex-1 truncate font-mono text-[11px] text-gray-200'>
              {n.label}
            </span>
            {n.intralink && (
              <Cable
                size={10}
                className='shrink-0 text-amber-400/80'
                aria-label={t('layout.nodeLan', 'Interní síť (LAN)')}
              />
            )}
            <EnvBadge env={n.env} />
            {n.current && <Check size={11} className='text-plex shrink-0' />}
          </button>
        ))}
      </div>,
      document.body,
    )
    : null;

  const nameBtn = canUi
    ? (
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen((v) => !v)}
        title={t('layout.nodeSwitchAria', 'Přepnout nod')}
        aria-haspopup='listbox'
        aria-expanded={open}
        className={phone
          ? 'phone-node-switch-btn min-w-0 flex-1 inline-flex items-center gap-0.5 text-left'
          : 'min-w-0 flex-1 inline-flex items-center gap-1 text-left'}
      >
        <span
          className={phone
            ? 'text-[15px] font-semibold truncate flex-1 text-gray-100'
            : 'text-xs text-gray-500 font-mono truncate flex-1'}
          title={displayLabel}
        >
          {displayLabel}
        </span>
        <ChevronDown
          size={phone ? 16 : 11}
          className={`shrink-0 transition-transform ${phone ? 'text-gray-500' : 'text-gray-600'} ${open ? 'rotate-180' : ''}`}
        />
      </button>
    )
    : (
      <span
        className={phone
          ? 'text-[15px] font-semibold truncate flex-1 text-gray-100'
          : 'text-xs text-gray-500 font-mono truncate flex-1'}
        title={displayLabel}
      >
        {displayLabel}
      </span>
    );

  const homeBtn = showHome
    ? (
      <button
        type='button'
        disabled={!homeHref}
        onClick={() => {
          if (!homeHref) return;
          void goPublic(homeHref);
        }}
        title={homeHref
          ? t('layout.nodeHome', 'Domů — {{node}}', { node: home.label || info?.spoke_hub || '' })
          : t('layout.nodeHomeMissing', 'Domů — veřejné UI hubu není známé')}
        className={`shrink-0 rounded-md p-0.5 ${
          homeHref
            ? 'text-gray-500 hover:bg-gray-800/70 hover:text-gray-300'
            : 'text-gray-700 cursor-default opacity-40'
        }`}
      >
        <Home size={phone ? 16 : 12} />
      </button>
    )
    : null;

  if (phone) {
    return (
      <div className='phone-node-switch min-w-0 flex-1 flex items-center gap-0.5'>
        {nameBtn}
        {homeBtn}
        {menu}
      </div>
    );
  }

  return (
    <div className='flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-gray-900/40 border border-gray-800/60 min-w-0'>
      <Server size={13} className='text-gray-400 shrink-0' />
      {nameBtn}
      {homeBtn}
      <EnvBadge env={displayEnv} label={info?.label} />
      {menu}
    </div>
  );
}
